import { baseApi } from '@klp/core/api';

interface FileParams {
    filePath: string;
    fileName: string;
    fileContentField?: string;
    queryParams?: object;
}

interface FileResponse {
    blob: Blob;
    fileName: string;
}

const b64toBlob = (b64Data: string, contentType = '', sliceSize = 512) => {
    const byteCharacters = atob(b64Data);
    const byteArrays: Array<Uint8Array> = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays as Array<BlobPart>, { type: contentType });
    return blob;
};

export const fileDownloadApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        downloadFile: builder.query<FileResponse, FileParams>({
            query: ({ filePath, fileContentField, queryParams }) => {
                // If fileContentField exists, we need JSON response
                if (fileContentField) {
                    return {
                        url: filePath,
                        params: queryParams,
                    };
                }

                // Otherwise, get blob directly
                return {
                    url: filePath,
                    params: queryParams,
                    responseHandler: (response) => response.blob(),
                };
            },
            transformResponse: async (
                response: Blob | Record<string, unknown>,
                meta,
                arg: FileParams,
            ) => {
                const { fileName, fileContentField } = arg;

                // Handle JSON response with base64 encoded file
                if (fileContentField && !(response instanceof Blob)) {
                    const blob = b64toBlob(
                        response[fileContentField] as string,
                        (response.type as string) || '',
                    );

                    return {
                        blob,
                        fileName:
                            (response.fileName as string | undefined) ??
                            fileName,
                    };
                }

                // Handle direct blob response
                return {
                    blob: response as Blob,
                    fileName,
                };
            },
            // Don't cache downloads - each is unique
            keepUnusedDataFor: 0,
        }),
    }),
});

export const { useLazyDownloadFileQuery } = fileDownloadApi;
